import {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import type { InspectionTemplate, TemplateSection } from 'inspection-types';

import ChannelAdminContext from '../../../../pages/portal/admin/channel/ChannelAdminContext';
import { TEMPLATE_LIBRARY_PATH } from '../../navigation/useInspectionsNavigation';

import { useInspectionTemplateCustomizeExpandedState } from './useInspectionTemplateCustomizeExpandedState';
import { useInspectionTemplateCustomizeLabels } from './useInspectionTemplateCustomizeLabels';
import { useInspectionTemplateCustomizeRouteFetch } from './useInspectionTemplateCustomizeRouteFetch';
import { useInspectionTemplateCustomizeSectionObserver } from './useInspectionTemplateCustomizeSectionObserver';

export type InspectionTemplateCustomizePageViewModel = {
  listPageUrl: string;
  hasValidRouteContext: boolean;
  template: InspectionTemplate | undefined;
  loading: boolean;
  error: Error | undefined;
  called: boolean;
  sortedSections: TemplateSection[];
  expandedBySectionId: Record<string, boolean>;
  activeSectionId: string | null;
  mainColumnRefCallback: (el: HTMLDivElement | null) => void;
  toggleSection: (sectionId: string) => void;
  handleCancel: () => void;
  summary: string | null;
  lastUpdatedText: string;
  saveDisabledTitle: string;
  revertAllDisabledTitle: string;
  removeSectionDisabledTitle: string;
  removeItemDisabledTitle: string;
};

export function useInspectionTemplateCustomizePage(
  channelBasePath: string
): InspectionTemplateCustomizePageViewModel {
  const { t } = useTranslation();
  const history = useHistory();
  const { channel } = useContext(ChannelAdminContext);
  const { templateId } = useParams<{ templateId: string }>();
  const mainColumnRef = useRef<HTMLDivElement | null>(null);

  const { template, loading, error, called } =
    useInspectionTemplateCustomizeRouteFetch(channel?._id, templateId);

  const [expandedBySectionId, setExpandedBySectionId] = useState<
    Record<string, boolean>
  >({});
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  const listPageUrl = `${channelBasePath}/${TEMPLATE_LIBRARY_PATH}`;

  useInspectionTemplateCustomizeExpandedState({
    template,
    setExpandedBySectionId,
    setActiveSectionId,
  });

  const sortedSections = useMemo((): TemplateSection[] => {
    if (!template) {
      return [];
    }

    return [...template.sections].sort((a, b) => a.order - b.order);
  }, [template]);

  useInspectionTemplateCustomizeSectionObserver({
    template,
    sortedSections,
    mainColumnRef,
    setActiveSectionId,
  });

  const handleCancel = useCallback(() => {
    history.push(listPageUrl);
  }, [history, listPageUrl]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedBySectionId(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const mainColumnRefCallback = useCallback((el: HTMLDivElement | null) => {
    mainColumnRef.current = el;
  }, []);

  const labels = useInspectionTemplateCustomizeLabels(template, t);

  const hasValidRouteContext = Boolean(channel?._id && templateId);

  return {
    listPageUrl,
    hasValidRouteContext,
    template,
    loading,
    error,
    called,
    sortedSections,
    expandedBySectionId,
    activeSectionId,
    mainColumnRefCallback,
    toggleSection,
    handleCancel,
    summary: labels.summary,
    lastUpdatedText: labels.lastUpdatedText,
    saveDisabledTitle: labels.saveDisabledTitle,
    revertAllDisabledTitle: labels.revertAllDisabledTitle,
    removeSectionDisabledTitle: labels.removeSectionDisabledTitle,
    removeItemDisabledTitle: labels.removeItemDisabledTitle,
  };
}
